import React from 'react';
import Api from './api';

const SORT_TYPES = [
    'id',
    'first_name',
    'last_name',
    'email_address',
    'coaching_timestamp'
];

function getTimestamp(date, time, defaultDate) {
    if (!time) {
        time = '12:00';
    }

    const timespl = time.split(/[:\s\-]+/g);
    let mins = parseInt(timespl[0], 10);
    let secs = parseInt(timespl[1], 10);

    if (isNaN(mins)) {
        mins = 12;
    }

    if (isNaN(secs)) {
        secs = 0;
    }

    if (mins < 6) {
        mins += 12;
    }

    if (!date || !date.length) {
        return new Date(`${defaultDate} ${mins}:${secs}`).getTime();
    }

    return new Date(`${date} ${mins}:${secs}`).getTime();
}

export default class Store extends React.Component {
    constructor(props) {
        super(props);

        this.api = new Api(props.scheduleKey);

        this.state = Object.assign({}, props, {
            actions: this.getActions(),
            sortType: 'id',
            sortDirection: 'asc',
            loaded: false,
            schoolName: '',
            domains: [],
            eventDate: new Date(),
            eventDates: [],
            structures: [],
            structuresById: {},
            scheduleOrder: [],
            schedule: {},
            note: '',
            error: null,
            message: null
        });
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        this.api.load().then(results => {
            if (results.success) {
                const date = new Date();
                date.setTime(results.event_date * 1000);
                const dateString = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

                for (let key in results.schedule) {
                    results.schedule[key].coaching_timestamp = getTimestamp(
                        results.schedule[key].coaching_date,
                        results.schedule[key].coaching_time,
                        dateString
                    );

                    this.saveDomain(results.schedule[key].email_address);
                }

                const structuresById = {};

                results.structures.forEach(struc => structuresById[struc.id] = struc.name);

                this.setState({
                    loaded: true,
                    error: null,
                    message: null,
                    schedule: results.schedule,
                    schoolName: results.school_name,
                    structures: results.structures,
                    structuresById,
                    eventDate: date,
                    eventDates: results.event_dates,
                    note: results.note
                }, () => this.sortSchedule());
            } else {
                this.setState({ error: results.error });
            }
        });
    }

    getActions = () => {
        return {
            sortSchedule: this.setScheduleSort,
            saveCell: this.saveCell,
            saveCells: this.saveCells,
            createRow: this.createRow,
            deleteRow: this.deleteRow,
            saveNote: this.saveNote,
            deleteAll: this.deleteAll,
            handleImport: this.handleImport
        };
    }

    setScheduleSort = (type, direction) => {
        const dir = direction.toLowerCase() === 'desc' ? 'desc' : 'asc';

        if (SORT_TYPES.indexOf(type.toLowerCase()) >= 0) {
            this.setState({ sortType: type.toLowerCase(), sortDirection: dir }, () => this.sortSchedule());
        }
    }

    saveCells = ({ id, fields }) => {
        if (!id) {
            return;
        }

        const schedule = Object.assign({}, this.state.schedule[id]);

        this.api.saveFields({ id, fields });

        for (let key in fields) {
            schedule[key] = fields[key];
        }

        schedule.coaching_timestamp = getTimestamp(
            schedule.coaching_date,
            schedule.coaching_time,
            this.state.eventDates[0]
        );

        this.saveDomain(fields.email_address);

        this.setState({ schedule: Object.assign({}, this.state.schedule, { [id]: schedule }) });
    }

    saveCell = ({ id, field, value }) => {
        if (!id) {
            return;
        }

        const schedule = Object.assign({}, this.state.schedule[id]);

        schedule[field] = value;

        schedule.coaching_timestamp = getTimestamp(
            schedule.coaching_date,
            schedule.coaching_time,
            this.state.eventDates[0]
        );

        if (field === 'email_address') {
            this.saveDomain(value);
        }

        this.setState({ schedule: Object.assign({}, this.state.schedule, { [id]: schedule }) });

        this.api.saveField({ id, field, value });
    }

    createRow = (callback) => {
        this.api.createTeacher().then(result => {
            if (result.id) {
                const schedule = Object.assign({}, this.state.schedule);
                const scheduleOrder = this.state.scheduleOrder.slice();

                schedule[result.id] = { id: result.id };
                scheduleOrder.push(result.id);

                this.setState({ schedule, scheduleOrder }, () => {
                    callback(result.id);
                });
            }
        });
    }

    deleteRow = (id) => {
        this.api.removeTeacher(id).then((data) => {
            let schedule = Object.assign({}, this.state.schedule);
            let scheduleOrder = this.state.scheduleOrder.slice();

            delete schedule[id];

            const index = scheduleOrder.indexOf(id);

            if (index >= 0) {
                scheduleOrder = scheduleOrder.slice(0, index).concat(scheduleOrder.slice(index + 1));
            }

            this.setState({ schedule, scheduleOrder });
        });
    }

    deleteAll = () => {
        this.api.removeAllTeachers().then(() => {
            this.setState({ schedule: {}, scheduleOrder: [] });
        });
    }

    saveNote = (note) => {
        this.setState({ note });
        this.api.saveNotes({ note });
    }

    sortSchedule = () => {
        const schedules = [];
        const { schedule, sortType, sortDirection } = this.state;
        const switcher = sortDirection === 'asc' ? 1 : -1;

        const comparator = (a, b) => ((a[sortType] > b[sortType] ? 1 : (a[sortType] === b[sortType] ? 0 : -1)) * switcher);

        for (let key in schedule) {
            schedules.push(schedule[key]);
        }

        schedules.sort(comparator);

        this.setState({ scheduleOrder: schedules.map(sch => sch.id) });
    }

    saveDomain = (email) => {
        if (email) {
            let { domains } = this.state;
            const spl = email.split('@');

            if (spl.length === 2) {
                const domain = spl[1].toLowerCase();

                if (domain.length && domain.split('.').length > 1) {
                    for (let i = domains.length - 1; i >= 0; i--) {
                        const domainStored = domains[i];

                        if ((domain.indexOf(domainStored) === 0 && domainStored !== domain) || domainStored.indexOf(domain) === 0) {
                            domains = domains.slice(0, i).concat(domains.slice(i + 1));
                        }
                    }

                    if (domains.indexOf(domain) < 0) {
                        domains.push(domain);
                    }

                    this.setState({ domains });
                }
            }
        }
    }

    handleImport = (fromImport) => {
        const { spreadsheet } = fromImport;

        if (spreadsheet === undefined) {
            return;
        }

        const { data, selectedHeaders } = spreadsheet;

        const getRow = row => row
            .map((col, ind) => ({ [selectedHeaders[ind].value]: col }))
            .reduce((prev, next) => Object.assign({}, prev, next), {});

        if (data.length) {
            this.setState({ loaded: false, message: 'Importing schedule... Please wait.' });

            const finalizer = (function () {
                let remaining = data.length;
                return () => {
                    remaining--;
                    if (remaining === 0) {
                        setTimeout(() => this.load(), 2000);
                    }
                }
            }.bind(this))();

            for (let i = 0; i < data.length; i++) {
                this.createRow(id => {
                    const row = getRow(data[i]);
                    if (row.full_name && !(row.first_name && row.last_name)) {
                        const [first_name, ...last_name] = row.full_name.split(' ')
                        row.first_name = first_name;
                        row.last_name = last_name.join(' ');
                    }
                    this.saveCells({ id, fields: row });
                    finalizer();
                });
            }
        }
    }

    render() {
        return (
            <div>
                { this.props.render(this.state) }
            </div>
        );
    }
}
